import {useCallback, useEffect, useRef, useState} from 'react';
import {generatePath, useLocation, useNavigate} from 'react-router';
import {type InfiniteData, useQueryClient} from '@tanstack/react-query';
import {routes} from '../../../routing/routes';
import {
    type ADKEvent,
    type AgentOption,
    type ChatMessage,
    type ChatSession,
    CONSOLIDATION_PROGRESS_STATE_KEY,
    CONSOLIDATION_TOOL_NAMES,
    type ConsolidationToolProgress,
    type EmbeddedDashboardView,
    type SessionListResponse,
} from '../types/chatTypes';
import {useStores} from "../../../stores";
import {CHAT_CONFIG} from "../services/ChatConfiguration";
import {isDashgenAppName} from "../utils/agentDatabag";
import {
    appendThought,
    applyFunctionResponse,
    applyLiveText,
    buildMessagesFromADKEvents,
    endInterruptedTurn,
    generateMessageId,
    isFinalResponse,
    mapADKEventToChatMessages,
    markTurnEnded,
    stoppedMarker,
    toLiveEvent,
} from "~/pages/chat-agent/utils/adkEventMapping";
import {mapBackendSession, mergeServerSessions} from "~/pages/chat-agent/utils/chatSessions";
import {buildRequestInputResponse, findPendingInput} from "~/pages/chat-agent/utils/requestInput";
import {sessionListQueryKey, useSessionListQuery} from "./useSessionListQuery";
import {hasFunctionResponse, mergeSnapshotWithStreamed} from "~/pages/chat-agent/utils/streamedMessages";

const CHAT_PATH_RE = /\/chat\/?$/;

function searchWithSessionId(currentSearch: string, sessionId: string | null): string | undefined {
    const next = new URLSearchParams(currentSearch);
    const current = next.get('session_id');
    if (sessionId) {
        if (current === sessionId) {
            return undefined;
        }
        next.set('session_id', sessionId);
    } else {
        if (!current) {
            return undefined;
        }
        next.delete('session_id');
    }
    const query = next.toString();
    return query ? `?${query}` : '';
}

function getActionsStateDelta(event: ADKEvent): Record<string, unknown> | undefined {
    const delta = event.actions?.stateDelta;
    return delta && typeof delta === 'object' ? delta : undefined;
}

/** Latest message carrying an embedded dashboard, if any. */
function findLatestEmbeddedMessage(messages: ChatMessage[]): ChatMessage | null {
    for (let i = messages.length - 1; i >= 0; i--) {
        if (messages[i].embedded) return messages[i];
    }
    return null;
}

/**
 * The side panel for a chat's `messages`: its latest dashboard. The panel already showing that dashboard
 * keeps its refreshToken, so the iframe only remounts when the agent edits it (see handleStreamingEvent).
 */
function latestDashboardView(prev: EmbeddedDashboardView | null, messages: ChatMessage[]): EmbeddedDashboardView | null {
    const embedded = findLatestEmbeddedMessage(messages)?.embedded;
    if (!embedded) return null;
    return {...embedded, refreshToken: prev?.uuid === embedded.uuid ? prev.refreshToken : 0};
}

function findLatestConsolidationCallIndex(messages: ChatMessage[]): number {
    for (let i = messages.length - 1; i >= 0; i--) {
        const m = messages[i];
        if (m.event_type === 'function_call' && CONSOLIDATION_TOOL_NAMES.has(m.content)) {
            return i;
        }
    }
    return -1;
}

function applyConsolidationStateDelta(messages: ChatMessage[], delta: Record<string, unknown>): ChatMessage[] {
    const keyPayload = delta[CONSOLIDATION_PROGRESS_STATE_KEY];
    if (keyPayload == null || typeof keyPayload !== 'object') {
        return messages;
    }
    const o = keyPayload as Record<string, unknown>;
    const idx = findLatestConsolidationCallIndex(messages);
    if (idx < 0) {
        return messages;
    }
    if (o.stage === 'done') {
        const next = [...messages];
        const nextMsg = {...next[idx]};
        delete nextMsg.toolProgress;
        next[idx] = nextMsg;
        return next;
    }
    const num = (v: unknown): number | undefined =>
        typeof v === 'number' && !Number.isNaN(v) ? v : undefined;
    const toolProgress: ConsolidationToolProgress = {
        stage: String(o.stage ?? ''),
        completed: Number(o.completed) || 0,
        total: Math.max(Number(o.total) || 1, 1),
        detail: typeof o.detail === 'string' ? o.detail : undefined,
        stageNumber: num(o.stage_number) ?? num((o as {stageNumber?: unknown}).stageNumber),
        stageTotal: num(o.stage_total) ?? num((o as {stageTotal?: unknown}).stageTotal) ?? 4,
    };
    const next = [...messages];
    next[idx] = {...next[idx], toolProgress};
    return next;
}

// TODO: QA: Limit file size to ~300 lines
// TODO: QA: Separate communication layer and UI management
// TODO: QA: This component is not responding to muix updated for some unkown reason.
//  Therefor `bagStore.bagId` updates are being propagated as arguments and observables to ensure startNewChat() works from external components as well

// Do not import directly, use useChatContext (TODO: refactor to remove 'export')
export const useChat = () => {
    const {bagStore, chatApiStore} = useStores();
    const queryClient = useQueryClient();
    const location = useLocation();
    const navigate = useNavigate();
    const locationRef = useRef(location);
    const [sessions, setSessions] = useState<ChatSession[]>([]);
    const sessionsRef = useRef(sessions);
    const {
        data: sessionListData,
        isPending: sessionsPending,
        isFetched: sessionsListReady,
        fetchNextPage,
        hasNextPage,
        isFetchingNextPage,
    } = useSessionListQuery(bagStore.bagId || undefined);
    const [currentSession, setCurrentSession] = useState<ChatSession | null>(null);
    /**
     * Sessions with an agent turn (run_sse) in flight in this tab, by session id ('temp' until it is created).
     * Kept per session here (ChatProvider is app level), so a turn keeps its "in progress" state while the user
     * switches to another chat and back. Leaving a chat never cancels its stream.
     */
    const [streamingIds, setStreamingIds] = useState<ReadonlySet<string>>(new Set());
    /** The same set for async callbacks. Updated together with the state, so a check right after start sees it. */
    const streamingIdsRef = useRef(streamingIds);
    const updateStreamingIds = useCallback((change: (ids: Set<string>) => void) => {
        const next = new Set(streamingIdsRef.current);
        change(next);
        streamingIdsRef.current = next;
        setStreamingIds(next);
    }, []);
    const startStream = useCallback((sessionId: string) => {
        updateStreamingIds(ids => {
            ids.add(sessionId);
        });
    }, [updateStreamingIds]);
    /** Moves a stream from the temporary session to the id the backend created for it. */
    const renameStream = useCallback((fromId: string, toId: string) => {
        updateStreamingIds(ids => {
            if (ids.delete(fromId)) ids.add(toId);
        });
    }, [updateStreamingIds]);
    const finishStream = useCallback((sessionId: string) => {
        updateStreamingIds(ids => {
            ids.delete(sessionId);
        });
    }, [updateStreamingIds]);
    /** For async callbacks: reads the current set, not the one of the render that created the callback. */
    const isStreamingNow = useCallback((sessionId: string | null | undefined) =>
        sessionId != null && streamingIdsRef.current.has(sessionId), []);
    /** For rendering. */
    const isStreaming = useCallback((sessionId: string | null | undefined) =>
        sessionId != null && streamingIds.has(sessionId), [streamingIds]);
    const hasReceivedFinalMessage = !isStreaming(currentSession?.id);
    const hasActiveStream = streamingIds.size > 0;
    const [isRestoringSession, setIsRestoringSession] = useState(false);
    const [availableAgents, setAvailableAgents] = useState<AgentOption[]>([]);
    const [selectedAppName, setSelectedAppNameState] = useState<string>(CHAT_CONFIG.APP_NAME);
    const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
    const currentSessionRef = useRef<ChatSession | null>(null);
    /** The in-flight run of each session, by session id ('temp' until it is created); stopResponse aborts it. */
    const runControllersRef = useRef(new Map<string, AbortController>());
    /**
     * Sessions still streaming when the databag was switched: the new bag's list drops them, so their
     * events are lost meanwhile, and opening one again reloads it from the backend.
     */
    const incompleteSessionIdsRef = useRef(new Set<string>());
    /**
     * The chat the user last asked to open (selectSession, restoreSession), or null once they leave it for
     * a new chat, another agent or databag. A transcript load that ends after they moved on is dropped, and
     * the URL-restore effect skips the chat already being opened. (currentSessionRef can't tell: it follows
     * the rendered chat.)
     */
    const openingSessionIdRef = useRef<string | null>(null);
    /** session_id the app navigated to itself; until the URL shows it, the URL-restore effect sees a stale id. */
    const pendingUrlSessionIdRef = useRef<string | null | undefined>(undefined);
    const prevBagIdRef = useRef(0);
    const isWaiting = Boolean(currentSession?.messages.length) && !hasReceivedFinalMessage;
    /** Superset dashboard shown in the right-side panel (latest from the agent). */
    const [embeddedDashboard, setEmbeddedDashboard] = useState<EmbeddedDashboardView | null>(null);
    const [syncedSessionListData, setSyncedSessionListData] = useState(sessionListData);
    const [syncedBagId, setSyncedBagId] = useState(bagStore.bagId);

    // Keep refs in sync for async callbacks without reading them during render.
    useEffect(() => {
        locationRef.current = location;
    }, [location]);
    useEffect(() => {
        sessionsRef.current = sessions;
    }, [sessions]);
    useEffect(() => {
        currentSessionRef.current = currentSession;
    }, [currentSession]);

    // Docked chat (Dashboards module's Atlas panel): keep the session out of the URL so sending a
    // message never navigates away from the page hosting the panel.
    const dockedRef = useRef(false);
    const setChatDocked = useCallback((docked: boolean) => {
        dockedRef.current = docked;
    }, []);

    const syncSessionToUrl = useCallback((sessionId: string | null) => {
        if (dockedRef.current) {
            return;
        }
        const {pathname, search} = locationRef.current;
        const nextSearch = searchWithSessionId(search, sessionId);
        if (nextSearch === undefined) {
            return;
        }

        let pathnameTarget = pathname;
        if (sessionId && !CHAT_PATH_RE.test(pathname)) {
            const bagId = bagStore.bagId;
            if (!bagId) {
                return;
            }
            pathnameTarget = generatePath(routes.chat, {id: String(bagId)});
        } else if (!sessionId && !CHAT_PATH_RE.test(pathname)) {
            return;
        }

        pendingUrlSessionIdRef.current = sessionId;
        navigate({pathname: pathnameTarget, search: nextSearch}, {replace: true});
    }, [navigate, bagStore.bagId]);

    const setSelectedAppName = useCallback((appName: string) => {
        setSelectedAppNameState(appName);
        setIsSidebarCollapsed(isDashgenAppName(appName));
        // Clear current session — the session list already shows all agents so no reload needed
        openingSessionIdRef.current = null;
        currentSessionRef.current = null;
        setCurrentSession(null);
        setEmbeddedDashboard(null);
        syncSessionToUrl(null);
    }, [syncSessionToUrl]);

    useEffect(() => {
        chatApiStore.listApps().then(agents => {
            if (agents.length > 0) setAvailableAgents(agents);
        });
    }, [chatApiStore]);

    const loadMoreSessions = useCallback(async () => {
        if (!hasNextPage || isFetchingNextPage) return;
        await fetchNextPage();
    }, [fetchNextPage, hasNextPage, isFetchingNextPage]);

    /** Refresh sidebar titles from the paginated session list without clearing messages. */
    const refreshSessionDisplayNames = useCallback(async () => {
        if (!bagStore.bagId) return;
        try {
            await queryClient.invalidateQueries({queryKey: sessionListQueryKey(bagStore.bagId)});
        } catch (error) {
            console.error('[useChat] Error refreshing session display names:', error);
        }
    }, [bagStore.bagId, queryClient]);

    const patchSessionListCache = useCallback((
        updater: (data: InfiniteData<SessionListResponse>) => InfiniteData<SessionListResponse>,
    ) => {
        if (!bagStore.bagId) return;
        queryClient.setQueryData<InfiniteData<SessionListResponse>>(
            sessionListQueryKey(bagStore.bagId),
            (old) => (old ? updater(old) : old),
        );
    }, [bagStore.bagId, queryClient]);

    const updateSessionName = useCallback((sessionId: string, name: string) => {
        setSessions((prev) =>
            prev.map((s) => (s.id === sessionId ? {...s, name} : s)),
        );
        setCurrentSession((prev) =>
            prev?.id === sessionId ? {...prev, name} : prev,
        );
        patchSessionListCache((old) => ({
            ...old,
            pages: old.pages.map((page) => ({
                ...page,
                items: page.items.map((item) =>
                    item.session_id === sessionId ? {...item, display_name: name} : item,
                ),
            })),
        }));
    }, [patchSessionListCache]);

    const removeSession = useCallback((sessionId: string) => {
        setSessions((prev) => prev.filter((s) => s.id !== sessionId));
        patchSessionListCache((old) => {
            const removed = old.pages.some((page) =>
                page.items.some((item) => item.session_id === sessionId),
            );
            return {
                ...old,
                pages: old.pages.map((page) => ({
                    ...page,
                    items: page.items.filter((item) => item.session_id !== sessionId),
                    total: removed ? Math.max(0, page.total - 1) : page.total,
                })),
            };
        });

        if (openingSessionIdRef.current === sessionId) {
            openingSessionIdRef.current = null;
        }
        if (currentSessionRef.current?.id === sessionId) {
            currentSessionRef.current = null;
            setCurrentSession(null);
            setEmbeddedDashboard(null);
            syncSessionToUrl(null);
        }
    }, [patchSessionListCache, syncSessionToUrl]);

    useEffect(() => {
        if (!bagStore.bagId) return;

        const prevBagId = prevBagIdRef.current;
        const bagChanged = prevBagId !== 0 && prevBagId !== bagStore.bagId;
        prevBagIdRef.current = bagStore.bagId;

        if (bagChanged) {
            // A chat still being created ('temp') is added, with its question, to whichever list shows when
            // its session exists, so it loses nothing.
            streamingIdsRef.current.forEach(id => {
                if (id !== 'temp') incompleteSessionIdsRef.current.add(id);
            });
            openingSessionIdRef.current = null;
            currentSessionRef.current = null;
            setCurrentSession(null);
            setEmbeddedDashboard(null);
            syncSessionToUrl(null);
            setSelectedAppNameState(CHAT_CONFIG.APP_NAME);
            setIsSidebarCollapsed(false);
        }
    }, [bagStore.bagId, syncSessionToUrl]);

    /**
     * ADK attaches tool state_delta only to the final function_response, not mid-tool SSE.
     * LongRunningFunctionTool is for client-driven resume rounds, not in-loop streaming here.
     * Poll the agent API in-process store for every session with a response streaming.
     */
    useEffect(() => {
        if (!hasActiveStream) return;

        let cancelled = false;

        const pollSession = async (sessionId: string) => {
            if (cancelled || !isStreamingNow(sessionId)) return;

            const session = sessionsRef.current.find(s => s.id === sessionId);
            if (!session) return;
            const last = session.messages[session.messages.length - 1];
            if (
                !last ||
                last.event_type !== 'function_call' ||
                !CONSOLIDATION_TOOL_NAMES.has(last.content)
            ) {
                return;
            }

            try {
                const appName = session.appName || CHAT_CONFIG.APP_NAME;
                const progress = await chatApiStore.getTailSpendConsolidationProgress(sessionId, appName);
                if (cancelled || progress == null) return;
                setSessions(prev =>
                    prev.map(s => {
                        if (s.id !== sessionId) return s;
                        const idx = findLatestConsolidationCallIndex(s.messages);
                        if (idx < 0) return s;
                        if (progress.stage === 'done') {
                            const nextMsgs = [...s.messages];
                            const nextMsg = {...nextMsgs[idx]};
                            delete nextMsg.toolProgress;
                            nextMsgs[idx] = nextMsg;
                            return {...s, messages: nextMsgs, updated_at: new Date()};
                        }
                        const nextMsgs = [...s.messages];
                        nextMsgs[idx] = {...nextMsgs[idx], toolProgress: progress};
                        return {...s, messages: nextMsgs, updated_at: new Date()};
                    }),
                );
            } catch {
                /* ignore transient poll failures */
            }
        };

        const tick = () => streamingIdsRef.current.forEach(id => void pollSession(id));
        tick();
        const intervalId = window.setInterval(tick, 2000);
        return () => {
            cancelled = true;
            window.clearInterval(intervalId);
        };
    }, [hasActiveStream, chatApiStore, isStreamingNow]);

    /** `streamedIds`: ids of this run's events that arrived as partial chunks and still await their final event. */
    const handleStreamingEvent = useCallback((event: ADKEvent, sessionId: string, streamedIds: Set<string>) => {
        if (event.author === 'user') return;

        const delta = getActionsStateDelta(event);
        if (delta) {
            setSessions(prevSessions => {
                const activeSession = prevSessions.find(s => s.id === sessionId);
                if (!activeSession) return prevSessions;
                return prevSessions.map(session => {
                    if (session.id !== sessionId) return session;
                    const patched = applyConsolidationStateDelta(session.messages, delta);
                    if (patched === session.messages) return session;
                    return {...session, messages: patched, updated_at: new Date()};
                });
            });
        }

        // Chunks share their final event's id, and the final event repeats the thoughts they streamed.
        let thoughtsStreamed = false;
        if (event.partial === true) {
            streamedIds.add(event.id);
        } else {
            thoughtsStreamed = streamedIds.delete(event.id);
        }
        const newMessages = mapADKEventToChatMessages(toLiveEvent(event, thoughtsStreamed));
        // A final response with nothing to show (e.g. only a state change) still ends the turn.
        const endsTurnSilently = newMessages.length === 0 && isFinalResponse(event);
        if (newMessages.length === 0 && !endsTurnSilently) return;

        // A dashgen tool result carrying an embedded dashboard opens/updates the right-side panel —
        // only while its chat is open; switching back restores it from the session's messages.
        const embeddedMsg = findLatestEmbeddedMessage(newMessages);
        if (embeddedMsg?.embedded && currentSessionRef.current?.id === sessionId) {
            const embedded = embeddedMsg.embedded;
            // create/update mutate the dashboard (same embed uuid) — bump the token
            // so the iframe remounts; show_dashboard only reports, no reload needed.
            const isMutation = embeddedMsg.content !== 'show_dashboard';
            setEmbeddedDashboard(prev => ({
                ...embedded,
                refreshToken: prev?.uuid === embedded.uuid
                    ? prev.refreshToken + (isMutation ? 1 : 0)
                    : 0,
            }));
        }

        setSessions(prevSessions => {
            const activeSession = prevSessions.find(s => s.id === sessionId);
            if (!activeSession) return prevSessions;

            return prevSessions.map(session => {
                if (session.id !== sessionId) return session;

                let updatedMessages = [...session.messages];
                if (endsTurnSilently) {
                    markTurnEnded(updatedMessages);
                }

                // A chat reopened mid-run may load an event from the backend before its SSE copy arrives, so
                // calls and responses already shown are skipped.
                for (const msg of newMessages) {
                    if (msg.event_type === 'thought') {
                        appendThought(updatedMessages, msg);
                    } else if (msg.event_type === 'function_response') {
                        if (!hasFunctionResponse(updatedMessages, msg)) applyFunctionResponse(updatedMessages, msg);
                    } else if (msg.event_type === 'text' && msg.type === 'bot') {
                        // Keyed by event id, so text from another agent (a sub-agent handoff) starts its own bubble.
                        updatedMessages = applyLiveText(updatedMessages, msg, event.partial === true);
                    } else if (!updatedMessages.some(m => m.id === msg.id)) {
                        updatedMessages.push(msg);
                    }
                }

                return {...session, messages: updatedMessages, updated_at: new Date()};
            });
        });
    }, []);

    /**
     * Streams one agent run into the session; aborting `controller` stops it. With
     * `retractOnEarlyFailure`, a run that fails before any event arrived never reached the agent, so
     * the sent bubble `sentMessageId` is removed again.
     */
    const streamIntoSession = useCallback(async (
        sessionId: string,
        openStream: (signal: AbortSignal) => AsyncGenerator<ADKEvent>,
        controller: AbortController,
        sentMessageId: string,
        retractOnEarlyFailure: boolean,
    ) => {
        let receivedEvent = false;
        const streamedIds = new Set<string>();
        try {
            for await (const event of openStream(controller.signal)) {
                receivedEvent = true;
                handleStreamingEvent(event, sessionId, streamedIds);
            }
        } catch (error) {
            // A stop is the user's choice, not a failure: what arrived stays and the turn ends as stopped.
            // A stopped answer is kept too, since it may already have reached the agent.
            const stopped = controller.signal.aborted;
            let ending: ChatMessage;
            if (stopped) {
                ending = stoppedMarker();
            } else {
                console.error('SSE streaming error:', error);
                ending = {
                    id: generateMessageId(),
                    type: 'bot',
                    content: `Error: ${error instanceof Error ? error.message : 'Connection failed'}`,
                    event_type: 'text',
                    isError: true,
                    timestamp: new Date(),
                };
            }
            const retract = !stopped && retractOnEarlyFailure && !receivedEvent;
            setSessions(prev => prev.map(s => s.id === sessionId
                ? {
                    ...s,
                    messages: endInterruptedTurn(
                        retract ? s.messages.filter(m => m.id !== sentMessageId) : s.messages,
                        ending,
                    ),
                    updated_at: new Date(),
                }
                : s));
        } finally {
            if (runControllersRef.current.get(sessionId) === controller) {
                runControllersRef.current.delete(sessionId);
            }
            finishStream(sessionId);
            await refreshSessionDisplayNames();
        }
    }, [finishStream, handleStreamingEvent, refreshSessionDisplayNames]);

    /**
     * Stops the open chat's run: the connection closes, so the agent server stops the run, and the
     * turn ends as stopped by the user.
     */
    const stopResponse = useCallback(() => {
        const sessionId = currentSessionRef.current?.id;
        if (sessionId) {
            runControllersRef.current.get(sessionId)?.abort();
        }
    }, []);

    const sendMessage = useCallback(async (content: string, bagId?: number) => {
        if (!content.trim()) return;
        if (isStreamingNow(currentSessionRef.current?.id ?? 'temp')) {
            console.warn('Still waiting on previous response, cannot send new message yet.');
            return;
        }
        bagId = bagId ?? bagStore.bagId;
        if (!bagId) {
            console.error('Cannot send message: bagId is missing');
            return;
        }

        let session = currentSessionRef.current;
        /** While the agent is paused on a question, the message is its answer and resumes that call. */
        const pendingInput = session ? findPendingInput(session.messages) : null;

        const userMessage: ChatMessage = {
            id: generateMessageId(),
            type: 'user',
            content,
            timestamp: new Date(),
        };

        startStream(session?.id ?? 'temp');
        // Exists from the start, so Stop also works while a new session is still being created.
        const controller = new AbortController();
        runControllersRef.current.set(session?.id ?? 'temp', controller);

        if (!session) {
            // Create temp session for UI, then create real session
            const tempSession: ChatSession = {
                id: 'temp',
                userId: '',
                name: 'New chat',
                messages: [userMessage],
                created_at: new Date(),
                updated_at: new Date(),
                appName: selectedAppName,
            };

            setSessions(prev => [...prev, tempSession]);
            currentSessionRef.current = tempSession;
            setCurrentSession(tempSession);

            try {
                const preferredAgent = selectedAppName === 'atlas' ? undefined : selectedAppName;
                const {sessionId, userId} = await chatApiStore.createSession(bagId, selectedAppName, preferredAgent);

                if (!sessionId) {
                    const errorMessage: ChatMessage = {
                        id: generateMessageId(),
                        type: 'bot',
                        content: 'Error: Failed to create a chat session. Please try again.',
                        event_type: 'text',
                        isError: true,
                        timestamp: new Date(),
                    };
                    setSessions(prev => prev.map(s =>
                        s.id === tempSession.id
                            ? {...s, messages: [...s.messages, errorMessage]}
                            : s
                    ));
                    runControllersRef.current.delete(tempSession.id);
                    finishStream(tempSession.id);
                    return;
                }

                runControllersRef.current.delete(tempSession.id);
                runControllersRef.current.set(sessionId, controller);
                const realSession: ChatSession = {
                    id: sessionId,
                    userId: userId,
                    name: tempSession.name,
                    messages: [userMessage],
                    created_at: tempSession.created_at,
                    updated_at: new Date(),
                    appName: selectedAppName,
                };

                setSessions(prev => {
                    const withoutTemp = prev.filter(s => s.id !== tempSession.id);
                    return [...withoutTemp, realSession];
                });
                renameStream(tempSession.id, sessionId);
                // The user may have switched chats while the session was being created — don't pull them back.
                if (currentSessionRef.current?.id === tempSession.id) {
                    currentSessionRef.current = realSession;
                    setCurrentSession(realSession);
                    syncSessionToUrl(sessionId);
                }
                session = realSession;
            } catch (error) {
                const errorMessage: ChatMessage = {
                    id: generateMessageId(),
                    type: 'bot',
                    content: `Error: Failed to create a chat session: ${error instanceof Error ? error.message : 'Unknown error'}. Please try again.`,
                    event_type: 'text',
                    isError: true,
                    timestamp: new Date(),
                };
                setSessions(prev => prev.map(s =>
                    s.id === tempSession.id
                        ? {...s, messages: [...s.messages, errorMessage]}
                        : s
                ));
                runControllersRef.current.delete(tempSession.id);
                finishStream(tempSession.id);
                return;
            }
        } else {
            setSessions(prev => {
                const updated = prev.map(s =>
                    s.id === session!.id
                        ? {...s, messages: [...s.messages, userMessage], updated_at: new Date()}
                        : s
                );
                const updatedSession = updated.find(s => s.id === session!.id);
                if (updatedSession) {
                    currentSessionRef.current = updatedSession;
                    setCurrentSession(updatedSession);
                }
                return updated;
            });
        }

        // Stopped while the session was being created: nothing reached the agent.
        if (controller.signal.aborted) {
            runControllersRef.current.delete(session!.id);
            setSessions(prev => prev.map(s => s.id === session!.id
                ? {...s, messages: endInterruptedTurn(s.messages, stoppedMarker()), updated_at: new Date()}
                : s));
            finishStream(session!.id);
            return;
        }

        // Stream agent response via SSE — use the session's own appName for routing. An answer that
        // fails before any event never reached the agent, so it is retracted and the question reopens.
        await streamIntoSession(
            session!.id,
            signal => pendingInput
                ? chatApiStore.sendFunctionResponseSSE(
                    session!.id,
                    session!.userId,
                    session!.appName,
                    buildRequestInputResponse(pendingInput, content),
                    signal,
                )
                : chatApiStore.sendMessageSSE(session!.id, session!.userId, content, session!.appName, signal),
            controller,
            userMessage.id,
            Boolean(pendingInput),
        );
    }, [chatApiStore, isStreamingNow, startStream, renameStream, finishStream, bagStore.bagId, selectedAppName, streamIntoSession, syncSessionToUrl]);

    const selectSession = useCallback(async (session: ChatSession) => {
        // Point the ref at the clicked chat now — async callbacks (session creation, streamed
        // dashboards) must not treat the chat being left as open while its detail loads.
        openingSessionIdRef.current = session.id;
        currentSessionRef.current = session;
        if (session.id !== 'temp') {
            syncSessionToUrl(session.id);
        }
        setIsSidebarCollapsed(isDashgenAppName(session.appName));
        setIsRestoringSession(true);
        await Promise.resolve();

        try {
            // Local messages are already up to date, and a live stream keeps writing into them.
            if (session.messages.length > 0 && !incompleteSessionIdsRef.current.has(session.id)) {
                setCurrentSession(session);
                setEmbeddedDashboard(prev => latestDashboardView(prev, session.messages));
                return;
            }

            // No local messages, or only the tail of a run that kept streaming across a databag switch —
            // so load the backend snapshot.
            const sessionDetail = await chatApiStore.getSessionDetail(session.id, session.appName);
            const messages = buildMessagesFromADKEvents(sessionDetail.events);
            // The user moved on: the session keeps what it had, and opening it again loads it again.
            if (openingSessionIdRef.current !== session.id) return;
            incompleteSessionIdsRef.current.delete(session.id);

            // Any local messages streamed in after the session list was rebuilt, or while the snapshot loaded.
            // The open chat and its panel show them too; the stored session merges with its latest messages.
            const streamed = sessionsRef.current.find(s => s.id === session.id)?.messages ?? [];
            const updatedSession: ChatSession = {
                ...session,
                userId: session.userId || sessionDetail.userId || '',
                messages: mergeSnapshotWithStreamed(messages, streamed),
            };

            setSessions(prev => prev.map(s => s.id === session.id
                ? {...updatedSession, messages: mergeSnapshotWithStreamed(messages, s.messages)}
                : s));
            setCurrentSession(updatedSession);
            setEmbeddedDashboard(prev => latestDashboardView(prev, updatedSession.messages));
        } catch (error) {
            console.error('Error loading session messages:', error);
            if (openingSessionIdRef.current !== session.id) return;
            setCurrentSession(session);
            setEmbeddedDashboard(null);
        } finally {
            // A chat opened since then clears its own loading state.
            if (openingSessionIdRef.current === session.id || openingSessionIdRef.current === null) {
                setIsRestoringSession(false);
            }
        }
    }, [chatApiStore, syncSessionToUrl]);

    const startNewChat = useCallback((initialMessage?: string, bagId?: number) => {
        syncSessionToUrl(null);
        openingSessionIdRef.current = null;
        currentSessionRef.current = null;
        setCurrentSession(null);
        setEmbeddedDashboard(null);
        setSelectedAppNameState(CHAT_CONFIG.APP_NAME);

        if (initialMessage) {
            setTimeout(() => {
                sendMessage(initialMessage, bagId).then();
            }, 100);
        }
    }, [sendMessage, syncSessionToUrl]);

    /** Opens `sessionId`, loading it when needed; null when that fails or the user moved on meanwhile. */
    const restoreSession = useCallback(async (sessionId: string) => {
        openingSessionIdRef.current = sessionId;
        try {
            setIsRestoringSession(true);

            // Find the session in our list to know which app it belongs to
            const knownSession = sessionsRef.current.find(s => s.id === sessionId);
            const appName = knownSession?.appName ?? CHAT_CONFIG.APP_NAME;
            setIsSidebarCollapsed(isDashgenAppName(appName));

            // A live stream keeps writing into the local session — never replace it with a backend snapshot.
            // The snapshot is still needed without local messages, or when the run kept streaming across a
            // databag switch and the local session holds only its tail.
            const incomplete = incompleteSessionIdsRef.current.has(sessionId);
            if (knownSession && knownSession.messages.length > 0 && !incomplete && isStreamingNow(sessionId)) {
                currentSessionRef.current = knownSession;
                setCurrentSession(knownSession);
                setEmbeddedDashboard(prev => latestDashboardView(prev, knownSession.messages));
                return knownSession;
            }

            const sessionDetail = await chatApiStore.getSessionDetail(sessionId, appName);
            const messages = buildMessagesFromADKEvents(sessionDetail.events);
            // The user moved on: the session keeps what it had, and opening it again loads it again.
            if (openingSessionIdRef.current !== sessionId) return null;
            incompleteSessionIdsRef.current.delete(sessionId);
            const detailUserId = sessionDetail.userId || '';

            // Build the session BEFORE setSessions: assigning it inside the state
            // updater only works when React evaluates the updater eagerly (empty
            // queue, e.g. page reload) — on client-side navigation the updater is
            // deferred and the restored session was never made current.
            const existingSession = sessionsRef.current.find(s => s.id === sessionId);
            // Without local messages before the load, any present now streamed in while it loaded; an incomplete
            // session's are the tail of its run. Both are merged in — into the open chat and its panel too, and
            // the stored session merges with its latest messages. Otherwise the session was not streaming and
            // the snapshot already holds everything.
            const replaceLocal = Boolean(knownSession?.messages.length) && !incomplete;
            const shown = replaceLocal ? messages : mergeSnapshotWithStreamed(messages, existingSession?.messages ?? []);
            const updatedSession: ChatSession = existingSession
                ? {...existingSession, userId: existingSession.userId || detailUserId, messages: shown}
                : {
                    id: sessionId,
                    userId: detailUserId,
                    name: `Chat ${sessionsRef.current.length + 1}`,
                    messages: shown,
                    created_at: messages.length > 0 ? messages[0].timestamp : new Date(),
                    updated_at: messages.length > 0 ? messages[messages.length - 1].timestamp : new Date(),
                    appName,
                };
            setSessions(prev => prev.some(s => s.id === sessionId)
                ? prev.map(s => {
                    if (s.id !== sessionId) return s;
                    return replaceLocal
                        ? updatedSession
                        : {...updatedSession, messages: mergeSnapshotWithStreamed(messages, s.messages)};
                })
                : [...prev, updatedSession]);

            currentSessionRef.current = updatedSession;
            setCurrentSession(updatedSession);
            setEmbeddedDashboard(prev => latestDashboardView(prev, updatedSession.messages));

            return updatedSession;
        } catch (error) {
            console.error('Error restoring session:', error);
            return null;
        } finally {
            // A chat opened since then clears its own loading state.
            if (openingSessionIdRef.current === sessionId || openingSessionIdRef.current === null) {
                setIsRestoringSession(false);
            }
        }
    }, [chatApiStore, isStreamingNow]);

    const clearSessionLoading = useCallback(() => {
        setIsRestoringSession(false);
    }, []);

    // Read session_id from URL on chat route after the session list fetch has settled.
    const isChatRoute = CHAT_PATH_RE.test(location.pathname);

    useEffect(() => {
        if (!isChatRoute) {
            pendingUrlSessionIdRef.current = undefined;
            return;
        }
        if (!bagStore.bagId || !sessionsListReady || sessionsPending) {
            return;
        }

        const sessionIdFromUrl = new URLSearchParams(location.search).get('session_id');
        if (pendingUrlSessionIdRef.current !== undefined) {
            // Switching chats updates state before the URL catches up — don't restore the chat being left.
            if (sessionIdFromUrl !== pendingUrlSessionIdRef.current) return;
            pendingUrlSessionIdRef.current = undefined;
        }
        if (
            !sessionIdFromUrl ||
            isRestoringSession ||
            openingSessionIdRef.current === sessionIdFromUrl ||
            currentSessionRef.current?.id === sessionIdFromUrl
        ) {
            return;
        }

        void restoreSession(sessionIdFromUrl);
    }, [
        isChatRoute,
        bagStore.bagId,
        location.search,
        sessionsListReady,
        sessionsPending,
        isRestoringSession,
        restoreSession,
    ]);

    // Adjust state during render when query data / bag changes (React-recommended alternative to syncing in an effect).
    if (bagStore.bagId !== syncedBagId) {
        setSyncedBagId(bagStore.bagId);
        setSyncedSessionListData(sessionListData);
        // Cache hit: apply the new bag's pages now. Clearing without this leaves
        // sessions empty because sessionListData identity does not change again.
        if (sessionListData) {
            setSessions(sessionListData.pages.flatMap((page) =>
                page.items.map(mapBackendSession),
            ));
        } else {
            setSessions([]);
        }
    } else if (sessionListData !== syncedSessionListData) {
        setSyncedSessionListData(sessionListData);
        if (!bagStore.bagId) {
            setSessions([]);
        } else if (sessionListData) {
            const serverSessions = sessionListData.pages.flatMap((page) =>
                page.items.map(mapBackendSession),
            );
            setSessions((prev) => mergeServerSessions(prev, serverSessions));
        }
    }

    if (currentSession) {
        const updatedSession = sessions.find(s => s.id === currentSession.id);
        if (updatedSession && updatedSession !== currentSession) {
            const keepLocalMessages =
                currentSession.messages.length > 0 &&
                updatedSession.messages.length === 0;
            if (!keepLocalMessages) {
                setCurrentSession(updatedSession);
            }
        }
    }

    /** The question the current run is paused on, derived so live streaming and reload always agree. */
    const pendingInput = currentSession ? findPendingInput(currentSession.messages) : null;

    return {
        sessions,
        currentSession,
        pendingInput,
        hasReceivedFinalMessage,
        isStreaming,
        hasActiveStream,
        isWaiting,
        isRestoringSession,
        clearSessionLoading,
        availableAgents,
        selectedAppName,
        setSelectedAppName,
        setChatDocked,
        isSidebarCollapsed,
        setIsSidebarCollapsed,
        sendMessage,
        stopResponse,
        selectSession,
        startNewChat,
        restoreSession,
        updateSessionName,
        removeSession,
        loadMoreSessions,
        hasMoreSessions: Boolean(hasNextPage),
        sessionsPending,
        embeddedDashboard,
    };
};
